import { useMutation, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { Item } from "@/lib/erp";
import { useNavigate } from "react-router-dom";
import { routes } from "@/lib/config/routes";
import { itemQueryKeys } from "./itemQueryKeys";
import { useRef, useEffect } from "react";

/**
 * Hook for creating a new item
 * 
 * @param options - Configuration options
 * @param options.navigateOnSuccess - Whether to automatically navigate to detail page after creation (default: true)
 * @returns React Query mutation for creating item
 */
export function useCreateItem({ navigateOnSuccess = true }: { navigateOnSuccess?: boolean } = {}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const navigateRef = useRef(navigateOnSuccess);

  // Update ref when prop changes
  useEffect(() => {
    navigateRef.current = navigateOnSuccess;
  }, [navigateOnSuccess]);

  return useMutation<Item, Error, Record<string, any>>({
    mutationFn: (data: Record<string, any>) => {
      return erp.items.create(data);
    },
    onSuccess: (data) => {
      const itemName = data.name || data.item_code;
      
      // Update cache for the new item
      if (itemName) {
        queryClient.setQueryData(itemQueryKeys.detail(itemName), data);
      }
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: itemQueryKeys.list() });
      
      // Redirect to detail page only if navigation is enabled
      if (navigateRef.current && itemName) {
        navigate(routes.item.detail(itemName));
      }
    },
  });
}

